import { BacklinkDisplayType, DatabaseFieldType } from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import {
  type EntryLinkDatabaseResourceInput,
  type EntryLinkResolvedEntry
} from '../entry-link/entry-link-field-read-module.ts';
import {
  defineFieldReadModule,
  type FieldReadResource,
  type FieldReadDatabaseSnapshot,
  type FieldReadResourceState
} from '../field-read-module.ts';
import { type BacklinkReadContent } from './backlink-read-content.ts';
import { BacklinkValueView } from './backlink-value-view.tsx';

export type BacklinkReadSnapshot =
  | {
      readonly status: 'ready';
      readonly read: (
        database: FieldReadDatabaseSnapshot,
        entryId: string,
        includeLinks: boolean
      ) => { readonly count: number; readonly links: readonly EntryLinkResolvedEntry[] };
    }
  | { readonly status: 'broken' | 'restricted' };
export type BacklinkReadPolicy = {
  readonly brokenLabel: string;
  readonly restrictedLabel: string;
  readonly unnamedLabel: string;
  readonly getDatabaseSnapshot?: (
    input: EntryLinkDatabaseResourceInput
  ) => BacklinkReadSnapshot | undefined;
  readonly getDatabaseResource?: (
    input: EntryLinkDatabaseResourceInput
  ) => FieldReadResource<BacklinkReadSnapshot>;
};
export type BacklinkReadRenderer = (content: BacklinkReadContent) => ReactNode;
/**
 * Resolves one reverse relationship through the application's shared V1 index.
 */
export function createBacklinkFieldReadModule(
  options?: BacklinkReadPolicy & { readonly render?: BacklinkReadRenderer }
) {
  return defineFieldReadModule<BacklinkReadContent>({
    fieldType: DatabaseFieldType.BACKLINK,
    render:
      options?.render ?? ((content) => createElement(BacklinkValueView, { content })),
    compile(field) {
      const mode =
        field.settings.backlinkDisplayType === BacklinkDisplayType.NUMBER
          ? 'number'
          : 'link';
      const input = {
        contentId: field.settings.contentId,
        databaseId: field.settings.databaseId,
        databaseSpaceKey: field.settings.databaseSpace?.key
      };
      const configured = !!(
        input.contentId ||
        (input.databaseId && input.databaseSpaceKey)
      );
      const loadingWidth = field.width ? Math.min(field.width - 60, 100) : 100;
      const empty: BacklinkReadContent = Object.freeze({
        kind: 'backlink',
        mode,
        count: 0,
        entries: Object.freeze({ kind: 'entry-link', links: Object.freeze([]) })
      });

      return Object.freeze({
        fieldId: field.id,
        resolve: () => empty,
        getResourceRead({ database, address }) {
          if (!options || !configured) {
            return undefined;
          }

          const cached = options.getDatabaseSnapshot?.(input);
          const resource = cached ? undefined : options.getDatabaseResource?.(input);
          const source = cached ? { cached } : resource ? { resource } : undefined;

          if (!source) {
            return undefined;
          }

          const loading: BacklinkReadContent = Object.freeze({
            ...empty,
            entries: Object.freeze({ ...empty.entries, loadingWidth })
          });

          return Object.freeze({
            resources: Object.freeze(resource ? [resource] : []),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ): BacklinkReadContent {
              const state: FieldReadResourceState<BacklinkReadSnapshot> = source.cached
                ? { status: 'ready', value: source.cached }
                : read(source.resource);

              if (state.status === 'loading') {
                return loading;
              }

              if (state.status === 'ready' && state.value.status === 'ready') {
                const result = state.value.read(
                  database,
                  address.entryId,
                  mode === 'link'
                );

                return Object.freeze({
                  ...empty,
                  count: result.count,
                  entries: Object.freeze({
                    kind: 'entry-link',
                    links: Object.freeze(
                      result.links.map((link) =>
                        Object.freeze({
                          key: link.entryId,
                          entryId: link.entryId,
                          label: link.title || options.unnamedLabel,
                          href: link.href,
                          status: 'ready' as const
                        })
                      )
                    )
                  })
                });
              }

              if (state.status === 'unavailable') {
                return empty;
              }

              const message =
                state.status === 'ready' && state.value.status === 'restricted'
                  ? options.restrictedLabel
                  : options.brokenLabel;

              return Object.freeze({
                ...empty,
                message,
                entries: Object.freeze({
                  kind: 'entry-link',
                  links: Object.freeze([
                    {
                      key: 'error',
                      entryId: '',
                      label: message,
                      status: 'broken' as const,
                      href: undefined
                    }
                  ])
                })
              });
            }
          });
        }
      });
    }
  });
}
